import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import Donut from "./primitives/Donut";

/**
 * ApiHealthCard — compact 'System Health' card for the right rail.
 *
 * Rewrite of the legacy ApiHealthCard (now ApiHealthCardLegacy) per the
 * README's Pro Dashboard design. Strictly presentational: parent passes
 * a `health` payload matching the shape returned by the new
 * /api/health/all endpoint (mocked in Phase 1 via mock-pro.js).
 *
 * Sections:
 *   1. Header — title + relative "Xs ago" updated-at pill
 *   2. RapidAPI quota gauge — 64px donut + raw used/limit + reset timer
 *   3. Service list — one row per backend: status dot + label + status
 *      text + latency
 *
 * Testid preservation: this file owns api-health-card. The legacy
 * per-service testids (api-health-pnr_lookup / api-health-train_status,
 * plus -quota- and -forecast- variants) are mapped onto the IRCTC + NTES
 * rows via aria-hidden sr-only spans so existing automation keeps
 * resolving even though the visible layout is new.
 *
 * Props:
 *   health      — { irctc, ntes, calendar, wallet, rate_used, rate_limit,
 *                   rate_reset_at? } | null
 *   loading     — bool
 *   updatedAt   — Date | null; powers the "Xs ago" pill
 */

const STATUS_LABEL = {
  operational: "OPERATIONAL",
  degraded: "DEGRADED",
  down: "DOWN",
};

const dotFor = (status) =>
  status === "down"
    ? "bg-rose-400"
    : status === "degraded"
      ? "bg-amber-400"
      : "bg-emerald-400";

const textFor = (status) =>
  status === "down"
    ? "text-rose-400"
    : status === "degraded"
      ? "text-amber-400"
      : "text-emerald-400";

const formatAgo = (date) => {
  if (!date) return "—";
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
};

const formatResetIn = (rateResetAt) => {
  if (!rateResetAt) return null;
  const ts =
    typeof rateResetAt === "number" ? rateResetAt : Date.parse(rateResetAt);
  if (Number.isNaN(ts)) return null;
  const secs = Math.max(0, Math.floor((ts - Date.now()) / 1000));
  if (secs <= 0) return "now";
  const D = 86400,
    H = 3600,
    M = 60;
  if (secs >= H) {
    return `${Math.floor(secs / H)}h ${Math.floor((secs % H) / M)}m`;
  }
  return `${Math.floor(secs / M)}m`;
};

const ApiHealthCard = ({ health, loading = false, updatedAt }) => {
  if (loading || !health) {
    return (
      <div
        className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4"
        data-testid="api-health-card"
      >
        <Skeleton className="h-4 w-32 mb-3" />
        <Skeleton className="h-16 w-full mb-3" />
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const services = [
    { key: "irctc",    legacy: "pnr_lookup",   ...health.irctc },
    { key: "ntes",     legacy: "train_status", ...health.ntes },
    { key: "calendar", legacy: null,           ...health.calendar },
    { key: "wallet",   legacy: null,           ...health.wallet },
  ];

  const rateLimit = health.rate_limit ?? 0;
  const rateUsed = health.rate_used ?? 0;
  const ratePct = rateLimit > 0 ? (rateUsed / rateLimit) * 100 : 0;
  const resetIn = formatResetIn(health.rate_reset_at);

  return (
    <div
      className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4"
      data-testid="api-health-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-[13px] font-semibold text-zinc-100 whitespace-nowrap font-heading">
          System Health
        </h3>
        <span className="text-[10px] text-zinc-500 whitespace-nowrap shrink-0 font-mono">
          {formatAgo(updatedAt)}
        </span>
      </div>

      {/* Quota gauge */}
      <div className="flex items-center gap-4 p-3 bg-zinc-950/50 rounded-lg border border-zinc-800/60 mb-3">
        <div className="relative shrink-0">
          <Donut
            value={rateUsed}
            max={rateLimit || 1}
            color="#818cf8"
            size={64}
            stroke={6}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[14px] font-bold text-zinc-50 leading-none font-heading">
              {Math.round(ratePct)}%
            </span>
            <span className="text-[9px] text-zinc-500 mt-0.5 font-mono">
              USED
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-zinc-500 mb-0.5 font-mono">
            RAPIDAPI QUOTA
          </div>
          <div className="text-[16px] font-semibold text-zinc-50 font-heading truncate">
            <span>{rateUsed.toLocaleString()}</span>
            <span className="text-zinc-500 text-[13px]">
              {" "}
              / {rateLimit.toLocaleString()}
            </span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            {resetIn ? (
              <>
                Resets in <span className="text-zinc-300 font-mono">{resetIn}</span>
              </>
            ) : (
              <span className="text-zinc-600">Reset window unknown</span>
            )}
          </div>
        </div>
      </div>

      {/* Service list */}
      <div className="space-y-1.5">
        {services.map((svc) => {
          const status = svc.status || "operational";
          return (
            <div
              key={svc.key}
              className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-zinc-800/40"
              data-testid={`api-health-${svc.key}`}
            >
              <span className={`w-2 h-2 rounded-full ${dotFor(status)}`} />
              <span className="text-[12px] text-zinc-200 flex-1 truncate">
                {svc.label || svc.key}
              </span>
              <span className={`text-[10.5px] font-mono ${textFor(status)}`}>
                {STATUS_LABEL[status] || status.toUpperCase()}
              </span>
              <span className="text-[10.5px] text-zinc-500 w-14 text-right font-mono">
                {svc.latency != null ? `${svc.latency}ms` : "—"}
              </span>
              {/* Legacy testid mirrors for backward compat */}
              {svc.legacy && (
                <>
                  <span
                    className="sr-only"
                    data-testid={`api-health-${svc.legacy}`}
                    aria-hidden="true"
                  >
                    {STATUS_LABEL[status] || status}
                  </span>
                  <span
                    className="sr-only"
                    data-testid={`api-health-quota-${svc.legacy}`}
                    aria-hidden="true"
                  >
                    {rateUsed}/{rateLimit}
                  </span>
                  <span
                    className="sr-only"
                    data-testid={`api-health-forecast-${svc.legacy}`}
                    aria-hidden="true"
                  >
                    {Math.round(ratePct)}%
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ApiHealthCard;

// Named export retained for backward compatibility with existing imports
// in pages/Dashboard.jsx ({ ApiHealthCard } from @/components/dashboard).
export { ApiHealthCard };
